import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import type { PromotionContent } from '@wconcept/wconcept-common/models/renewal/promotion';
import { isStyleClipsModule } from '@components/pages/promotion/utils/typeGuards';

interface PromotionContentOrder {
  readonly contentId: string | number;
  readonly displayOrder: number;
}

/** URL 앵커와 네비 클릭 중 더 뒤의 순서까지 상품 모듈을 미리 로딩한다. */
export const getPromotionVisibleThroughOrder = (
  initialAnchorOrder?: number,
  pendingNavigationOrder?: number | null
): number | undefined => {
  const visibleThroughOrder = Math.max(
    initialAnchorOrder ?? 0,
    pendingNavigationOrder ?? 0
  );

  return visibleThroughOrder > 0 ? visibleThroughOrder : undefined;
};

/** 이동 위치에 영향을 주는 대상 이전 모듈의 콘텐츠 ID를 반환한다. */
export const getPromotionContentIdsThroughOrder = (
  contents: readonly PromotionContentOrder[],
  targetOrder: number
): ReadonlySet<string> =>
  new Set(
    contents
      .filter(content => content.displayOrder <= targetOrder)
      .map(content => String(content.contentId))
  );

interface UseVisibleModulesParams {
  contents: PromotionContent[];
  rootMargin?: string;
  threshold?: number;
  initialVisibleThroughOrder?: number;
}

interface VisibleModule {
  contentId: string;
  moduleType: string;
  isVisible: boolean;
  element: HTMLElement | null;
}

export const useVisibleModules = ({
  contents,
  rootMargin = '800px',
  threshold = 0.01,
  initialVisibleThroughOrder,
}: UseVisibleModulesParams) => {
  const visibleModulesRef = useRef<Map<string, VisibleModule>>(new Map());
  const [updateTrigger, setUpdateTrigger] = useState(0);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const moduleRefs = useRef<Map<string, HTMLElement>>(new Map());

  const contentsMap = useMemo(() => {
    const map = new Map<string, PromotionContent>();
    contents.forEach(content => {
      map.set(content.contentId.toString(), content);
    });
    return map;
  }, [contents.map(c => c.contentId).join(',')]); // contents 전체 대신 contentId만 비교

  const registerModule = useCallback(
    (contentId: string, element: HTMLElement | null) => {
      // console.log('🔍 모듈 등록 시도:', {
      //   contentId,
      //   element: !!element,
      //   elementTag: element?.tagName,
      //   elementId: element?.id,
      //   currentModuleCount: moduleRefs.current.size,
      //   hasObserver: !!observerRef.current,
      // });

      if (element) {
        moduleRefs.current.set(contentId, element);
        // console.log('✅ 모듈 등록 완료:', {
        //   contentId,
        //   newModuleCount: moduleRefs.current.size,
        //   registeredContentIds: Array.from(moduleRefs.current.keys()),
        // });

        // Observer가 있으면 즉시 관찰 시작
        if (observerRef.current) {
          element.setAttribute('data-content-id', contentId);
          observerRef.current.observe(element);
          // console.log('🔍 즉시 관찰 시작:', {
          //   contentId,
          //   element: element.tagName,
          // });
        } else {
          // console.warn('⚠️ Observer가 없어서 즉시 관찰할 수 없습니다:', {
          //   contentId,
          // });
        }

        // 모듈이 등록된 후 Observer 재설정 트리거
        setTimeout(() => {
          if (observerRef.current && moduleRefs.current.size > 0) {
            // console.log('🔍 지연된 Observer 재설정:', {
            //   moduleCount: moduleRefs.current.size,
            //   contentIds: Array.from(moduleRefs.current.keys()),
            // });

            observerRef.current.disconnect();
            moduleRefs.current.forEach((el, id) => {
              el.setAttribute('data-content-id', id);
              observerRef.current!.observe(el);
              // console.log('🔍 지연된 관찰 시작:', {
              //   contentId: id,
              //   element: el.tagName,
              // });
            });
          }
        }, 100);
      } else {
        moduleRefs.current.delete(contentId);
        // console.log('🗑️ 모듈 등록 해제:', {
        //   contentId,
        //   newModuleCount: moduleRefs.current.size,
        // });
      }
    },
    []
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // console.log('🔍 IntersectionObserver 생성:', {
    //   rootMargin,
    //   threshold,
    //   contentsLength: contents.length,
    // });

    observerRef.current = new IntersectionObserver(
      entries => {
        const updates: Array<{
          contentId: string;
          isVisible: boolean;
          element: HTMLElement;
        }> = [];

        // console.log('🔍 Intersection Observer 콜백 실행:', {
        //   entriesCount: entries.length,
        //   registeredModulesCount: moduleRefs.current.size,
        // });

        entries.forEach(entry => {
          let contentId = entry.target.getAttribute('data-content-id');
          if (!contentId) {
            for (const [id, element] of moduleRefs.current.entries()) {
              if (element === entry.target) {
                contentId = id;
                break;
              }
            }
          }
          if (!contentId) {
            // console.warn('⚠️ contentId를 찾을 수 없습니다:', {
            //   target: entry.target,
            //   dataContentId: entry.target.getAttribute('data-content-id'),
            //   registeredModules: Array.from(moduleRefs.current.keys()),
            // });
            return;
          }

          // 선택적 보강: ratio도 함께 확인 (플리킹 완화)
          const meet =
            entry.isIntersecting && entry.intersectionRatio >= threshold;

          // console.log('🔍 Intersection Observer 감지:', {
          //   contentId,
          //   isIntersecting: entry.isIntersecting,
          //   intersectionRatio: entry.intersectionRatio,
          //   threshold,
          //   meet,
          //   targetElement: entry.target.tagName,
          //   targetId: entry.target.id,
          // });

          updates.push({
            contentId,
            isVisible: meet,
            element: entry.target as HTMLElement,
          });
        });

        if (updates.length > 0) {
          let hasChanges = false;
          for (const { contentId, isVisible, element } of updates) {
            const existing = visibleModulesRef.current.get(contentId);
            if (existing?.isVisible !== isVisible) {
              const content = contentsMap.get(contentId);
              if (content) {
                visibleModulesRef.current.set(contentId, {
                  contentId,
                  moduleType: content.moduleType,
                  isVisible,
                  element,
                });
                hasChanges = true;
              }
            }
          }
          if (hasChanges) setUpdateTrigger(v => v + 1);
        }
      },
      { rootMargin, threshold }
    );

    // console.log('🔍 IntersectionObserver 설정 완료:', {
    //   rootMargin,
    //   threshold,
    //   observerExists: !!observerRef.current,
    // });

    // eslint-disable-next-line consistent-return
    return () => {
      // console.log('🔍 IntersectionObserver 정리');
      observerRef.current?.disconnect();
    };
  }, [contentsMap, rootMargin, threshold]);

  useEffect(() => {
    if (!observerRef.current) {
      // console.warn(
      //   '⚠️ Observer가 없습니다. Observer 생성이 완료되지 않았을 수 있습니다.'
      // );
      return;
    }

    // console.log('🔍 Observer 재설정:', {
    //   moduleCount: moduleRefs.current.size,
    //   contentIds: Array.from(moduleRefs.current.keys()),
    //   observerExists: !!observerRef.current,
    //   contentsMapSize: contentsMap.size,
    // });

    // 기존 관찰 중단
    observerRef.current.disconnect();

    // 등록된 모든 모듈을 다시 관찰 시작
    moduleRefs.current.forEach((element, contentId) => {
      element.setAttribute('data-content-id', contentId);
      observerRef.current!.observe(element);
      // console.log('🔍 요소 관찰 시작:', {
      //   contentId,
      //   element: element.tagName,
      //   elementId: element.id,
      // });
    });

    // 등록된 모듈이 없으면 경고
    if (moduleRefs.current.size === 0) {
      // console.warn(
      //   '⚠️ 등록된 모듈이 없습니다. 모듈 등록이 완료되지 않았을 수 있습니다.'
      // );
    } else {
      // console.log('✅ Observer 재설정 완료:', {
      //   observedModules: moduleRefs.current.size,
      //   contentIds: Array.from(moduleRefs.current.keys()),
      // });
    }
  }, [contentsMap]);

  // ✅ contentsMap이 바뀌면, 현재 보이는 모듈이 있는 경우 재계산 강제 트리거
  useEffect(() => {
    let hasVisible = false;
    for (const m of visibleModulesRef.current.values()) {
      if (m.isVisible) {
        hasVisible = true;
        break;
      }
    }
    if (hasVisible) setUpdateTrigger(v => v + 1);

    // 디버깅: contentsMap 변경 시 상태 로그
    // console.log('🔍 contentsMap 변경:', {
    //   contentsCount: contents.length,
    //   registeredModules: moduleRefs.current.size,
    //   visibleModules: Array.from(visibleModulesRef.current.values()).filter(
    //     m => m.isVisible
    //   ).length,
    // });
  }, [contentsMap]);

  const visibleItemCodes = useMemo(() => {
    const visibleContentIds = new Set(
      Array.from(visibleModulesRef.current.values())
        .filter(m => m.isVisible)
        .map(module => module.contentId)
    );

    // 딥링크 진입 시 대상 이전 모듈도 노출 처리해 상품 요청을 미리 시작한다.
    contentsMap.forEach(content => {
      if (
        initialVisibleThroughOrder !== undefined &&
        content.displayOrder <= initialVisibleThroughOrder
      ) {
        visibleContentIds.add(content.contentId.toString());
      }
    });

    return Array.from(visibleContentIds).reduce(
      (acc, contentId) => {
        const content = contentsMap.get(contentId);
        if (!content) return acc;

        const itemCodes: string[] = [];
        let itemStatusCd: Array<'01' | '02' | '04' | '05'> | undefined;

        switch (content.moduleType) {
          case 'PRODUCT': {
            // 탭 기반 상품 로딩으로 전환: ProductModule에서 usePromotionTabProducts 사용
            // 활성 탭의 상품만 조회하여 불필요한 API 호출 방지
            break;
          }
          case 'BRAND': {
            // 탭 기반 상품 로딩으로 전환: BrandModule에서 usePromotionTabProducts 사용
            // 활성 탭(스케줄)의 상품만 조회하여 불필요한 API 호출 방지
            break;
          }
          case 'SPECIAL_PRICE': {
            // 탭 기반 상품 로딩으로 전환: SpecialPriceModule에서 usePromotionTabProducts 사용
            // 활성 스케줄의 상품만 조회하여 불필요한 API 호출 방지
            break;
          }
          case 'FIRST_PURCHASE': {
            // 탭 기반 상품 로딩으로 전환: FirstPurchaseModule에서 usePromotionTabProducts 사용
            break;
          }
          case 'SHORT_FORM': {
            content.modules?.filter(isStyleClipsModule).forEach(v => {
              v.styleClips?.forEach(sc => {
                sc.itemCdList?.forEach(code => {
                  if (code) itemCodes.push(code);
                });
              });
            });
            itemStatusCd = ['04'];
            break;
          }
          // case 'RANKING':
          default:
            break;
        }

        if (itemCodes.length > 0) {
          acc[contentId] = {
            moduleType: content.moduleType,
            contentId,
            itemCodes: [...new Set(itemCodes)],
            itemStatusCd,
          };

          // console.log('🔍 visibleItemCodes에 추가:', {
          //   contentId: m.contentId,
          //   moduleType: content.moduleType,
          //   itemCodesCount: itemCodes.length,
          //   itemStatusCd,
          // });
        }
        return acc;
      },
      {} as Record<
        string,
        {
          moduleType: string;
          contentId: string;
          itemCodes: string[];
          itemStatusCd?: Array<'01' | '02' | '04' | '05'>;
        }
      >
    );
  }, [updateTrigger, contentsMap, initialVisibleThroughOrder]);

  return {
    visibleModules: visibleModulesRef.current,
    visibleItemCodes,
    registerModule,
    isModuleVisible: (contentId: string) => {
      const content = contentsMap.get(contentId);
      // 자체 쿼리를 가진 모듈도 대상 이전에 있으면 초기 화면에서 바로 로딩한다.
      const isBeforeInitialAnchor =
        initialVisibleThroughOrder !== undefined &&
        content !== undefined &&
        content.displayOrder <= initialVisibleThroughOrder;

      return (
        visibleModulesRef.current.get(contentId)?.isVisible ||
        isBeforeInitialAnchor
      );
    },
    // 필요 시 수동 재계산 훅(디버그용/강제 새로고침)
    forceRecompute: () => setUpdateTrigger(v => v + 1),
  };
};
